import classNames from "classnames"
import React, {
    FC,
    PropsWithChildren,
    useCallback,
    useEffect,
    useState,
} from "react"

import {
    soundController,
    setMusicMute,
    unsetMusicMute,
    getIsMute,
} from "@/assets/audios"
import { sfxPlay } from "@/assets/audios/sfx"
import returnButtonImg from "@/assets/imgs/icons/header_return.png"
import { useRewardLottery } from "@/components/Reward"
import { i18n } from "@/i18n"
import { requestCheckUser, requestLogout } from "@/services/user"
import { GlobalStage, useGlobalStore } from "@/store/global"
import { GlobalView, useGlobalView } from "@/store/view"
import { adapter } from "@/utils/sdk"
import { websdk } from "@/utils/websdk"
import { useRoleInfo, useSetUser } from "@/user/session"
import { registerLoginTrigger } from "@/utils/loginTrigger"

import {
    IconGift,
    IconHome,
    IconLogout,
    IconMusic,
    IconSwitch,
    IconUser,
} from "./icon"
import styles from "./index.module.scss"
import { showConfirm } from "@/components/Confirm"

const returnButton = adapter.ui.returnButton
const logoutButton = !returnButton
const inBilibiliSource = adapter.source.from === "bilibili"

export const Header = () => {
    const user = useRoleInfo()
    const setUser = useSetUser()
    const { globalStage, setGlobalStage } = useGlobalStore()
    const globalView = useGlobalView()

    const lotterySDK = useRewardLottery()

    useEffect(() => {
        if (user?.userId) {
            return () => {
                setGlobalStage(GlobalStage.LOADING)
            }
        }
    }, [user?.userId, setGlobalStage])

    const [enableMusic, setEnableMusic] = useState(getIsMute())

    const handleReturnClick = useCallback(() => {
        adapter.ui.onReturn()
    }, [])

    const handleLogin = useCallback(async () => {
        const result = await websdk.Login({ openQuickLogin: true })
        if (result.code !== 0 || !result.data) return
        const res = await requestCheckUser(result.data.LOGIN_UID, result.data.LOGIN_TOKEN)
        if (res.code === 0) {
            setUser(res.data.results)
        } else {
            if (res.code === 1001) {
                alert(i18n.get("common.error_user_not_found"))
            }
            await websdk.Logout()
        }
    }, [setUser])

    useEffect(() => {
        registerLoginTrigger(handleLogin)
    }, [handleLogin])

    const onLogout = () => {
        showConfirm({
            content: i18n.get("common.logoutConfirm"),
            onOk: async () => {
                await requestLogout()
                await websdk.Logout()
                window.location.reload()
            },
        })
    }

    lotterySDK?.refreshLotteryStatus()

    return (
        <>
            <header className={styles.header}>
                <div className={styles.buttons}>
                    {user ? (
                        <>
                            <div className={styles.loginBtn}>
                                <div className={styles.loginBtnContent}>
                                    <span>
                                        <b>{user.nickname}</b>
                                    </span>
                                    <div className={styles.iconGroup}>
                                        {logoutButton && (
                                            <IconLogout
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation()
                                                    sfxPlay("click")
                                                    onLogout()
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            <Button
                                title={i18n.get("header.gift")}
                                onClick={() => {
                                    lotterySDK?.start()
                                }}
                            >
                                <IconGift />
                            </Button>
                        </>
                    ) : (
                        <Button
                            onClick={() => {
                                handleLogin()
                            }}
                        >
                            <IconUser />
                        </Button>
                    )}
                    <Button
                        onClick={() => {
                            if (enableMusic) {
                                setMusicMute()
                                setEnableMusic(false)
                                soundController.disable()
                            } else {
                                unsetMusicMute()
                                setEnableMusic(true)
                                soundController.enable()
                            }
                        }}
                    >
                        <IconMusic off={!enableMusic} />
                    </Button>
                    {returnButton ? (
                        <Button
                            onClick={() => {
                                handleReturnClick()
                                sfxPlay("click")
                            }}
                        >
                            <img
                                className={classNames(styles.returnButton)}
                                src={returnButtonImg}
                                alt="return"
                            />
                        </Button>
                    ) : (
                        <a
                            href={
                                inBilibiliSource
                                    ? "https://www.biligame.com/detail/?id=101772"
                                    : "https://www.arknights.jp/"
                            }
                            target="_blank"
                            title={i18n.get("header.official")}
                            rel="noreferrer"
                        >
                            <Button>
                                <IconHome />
                            </Button>
                        </a>
                    )}
                </div>
            </header>
        </>
    )
}

const Button: FC<
    PropsWithChildren<{
        autoWidth?: boolean
        danger?: boolean
        onClick?: () => void
        title?: string
        classname?: string
    }>
> = ({ children, danger, autoWidth, onClick, title, classname }) => {
    return (
        <div
            title={title}
            className={classNames(
                styles.button,
                autoWidth && styles.autoWidth,
                danger && styles.danger,
                classname,
            )}
            onClick={() => {
                onClick?.()
                sfxPlay("click")
            }}
        >
            <div className={styles.contentBox}>{children}</div>
        </div>
    )
}
